import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { apiErr, API } from "@/lib/api";
import VideoPlayer from "@/components/VideoPlayer";
import SupportModal from "@/components/SupportModal";
import MediaCard from "@/components/MediaCard";
import { useAuth } from "@/context/AuthContext";
import { usePlayer } from "@/context/PlayerContext";
import { useBadges } from "@/context/BadgesContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
  Heart, Star, Bell, Share2, MessageCircle, BadgeCheck,
  Play, Pause, Loader2, Send, Trash2, Pencil, Flag, Clock, CalendarDays, Trophy, ChevronDown,
  Facebook, Youtube, Instagram, Twitter, Music2, Globe, Pin,
} from "lucide-react";

const SOCIAL_META = {
  facebook: { label: "Facebook", icon: Facebook, color: "hover:text-[#1877F2]" },
  youtube: { label: "YouTube", icon: Youtube, color: "hover:text-[#FF0000]" },
  instagram: { label: "Instagram", icon: Instagram, color: "hover:text-[#E4405F]" },
  tiktok: { label: "TikTok", icon: Music2, color: "hover:text-foreground" },
  x: { label: "X", icon: Twitter, color: "hover:text-foreground" },
  whatsapp: { label: "WhatsApp", icon: MessageCircle, color: "hover:text-[#25D366]" },
  telegram: { label: "Telegram", icon: Send, color: "hover:text-[#229ED9]" },
};
const normalizeSocialHref = (key, val) => {
  const v = (val || "").trim();
  if (!v) return "";
  if (/^https?:\/\//i.test(v)) return v;
  if (key === "whatsapp") return `https://wa.me/${v.replace(/[^0-9]/g, "")}`;
  return `https://${v.replace(/^\/+/, "")}`;
};

export default function MediaDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { current, playing, playRadio } = usePlayer();
  const { labelFor } = useBadges();
  const { t } = useTranslation();
  const [media, setMedia] = useState(null);
  const [comments, setComments] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [related, setRelated] = useState([]);
  const [reminders, setReminders] = useState(new Set());
  const [supportOpen, setSupportOpen] = useState(false);
  const [showGrid, setShowGrid] = useState(false);
  const [comment, setComment] = useState("");
  const [editing, setEditing] = useState(null);
  const [replyTo, setReplyTo] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/media/${slug}`);
      setMedia(data);
      api.post(`/media/${data.id}/view`).catch(() => {});
      const c = await api.get(`/media/${data.id}/comments`);
      setComments(c.data);
      api.get(`/media/${data.id}/schedule`).then((r) => setSchedule(r.data)).catch(() => {});
      api.get(`/media/${data.id}/related`).then((r) => setRelated(r.data.items || [])).catch(() => {});
    } catch { navigate("/"); }
  }, [slug, navigate]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (user && media) api.get("/me/reminders").then((r) => setReminders(new Set(r.data.map((x) => x.schedule_id)))).catch(() => {});
  }, [user, media]);

  const requireAuth = () => { if (!user) { toast.error(t("common.login_required")); navigate("/login"); return false; } return true; };

  const toggleReminder = async (s) => {
    if (!requireAuth()) return;
    try {
      if (reminders.has(s.id)) {
        await api.delete(`/me/reminders/${s.id}`);
        setReminders((prev) => { const n = new Set(prev); n.delete(s.id); return n; });
        toast.success(t("detail.reminder_off"));
      } else {
        await api.post(`/media/${media.id}/reminders`, { schedule_id: s.id });
        setReminders((prev) => new Set(prev).add(s.id));
        toast.success(t("detail.reminder_on"));
      }
    } catch (e) { toast.error(apiErr(e)); }
  };

  useEffect(() => {
    if (!media) return;
    const prevTitle = document.title;
    document.title = `${media.name} — ${media.type === "tv" ? "TV" : "Radio"} · Chaînes RDC`;
    const setMeta = (attr, key, val) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
      el.setAttribute("content", val);
    };
    setMeta("name", "description", (media.description || "").slice(0, 200));
    setMeta("property", "og:title", media.name);
    setMeta("property", "og:description", (media.description || "").slice(0, 200));
    setMeta("property", "og:image", media.banner || media.logo || "");
    setMeta("property", "og:url", window.location.href);
    return () => { document.title = prevTitle; };
  }, [media]);

  if (!media) return <div className="flex justify-center py-24"><Loader2 className="animate-spin text-primary" size={32} /></div>;

  const isTv = media.type === "tv";
  const isThisPlaying = current?.id === media.id && playing;
  const topLevel = comments.filter((c) => !c.parent_id);
  const repliesByParent = comments.reduce((acc, c) => {
    if (c.parent_id) (acc[c.parent_id] = acc[c.parent_id] || []).push(c);
    return acc;
  }, {});
  const fallback = `https://ui-avatars.com/api/?name=${encodeURIComponent(media.name)}&background=00A3E0&color=fff&bold=true&size=256`;

  const DAYS = ["Dimanche", "Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"];
  const now = new Date();
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const toMin = (t) => { const [h, m] = t.split(":").map(Number); return h * 60 + m; };
  const onAir = schedule.find((s) => {
    if (s.day_of_week !== now.getDay()) return false;
    let st = toMin(s.start_time), en = toMin(s.end_time);
    if (en <= st) en += 1440;
    return nowMin >= st && nowMin < en;
  });
  const todayGrid = schedule.filter((s) => s.day_of_week === now.getDay());

  const act = async (kind) => {
    if (!requireAuth()) return;
    try {
      const { data } = await api.post(`/media/${media.id}/${kind}`);
      setMedia((m) => {
        const nm = { ...m };
        if (kind === "like") { nm.is_liked = data.liked; nm.likes += data.liked ? 1 : -1; }
        if (kind === "favorite") { nm.is_favorite = data.favorite; nm.favorites += data.favorite ? 1 : -1; toast.success(data.favorite ? t("detail.favorite_added") : t("detail.favorite_removed")); }
        if (kind === "follow") { nm.is_following = data.following; nm.followers += data.following ? 1 : -1; toast.success(data.following ? t("detail.following") : t("detail.unfollowed")); }
        return nm;
      });
    } catch (e) { toast.error(apiErr(e)); }
  };

  const share = (platform) => {
    const canonical = window.location.href;
    const shareUrl = `${API}/share/${media.type}/${media.slug}`;
    const text = `Découvrez ${media.name} sur Les Chaînes Numériques de la RDC`;
    const links = {
      whatsapp: `https://wa.me/?text=${encodeURIComponent(text + " " + shareUrl)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      x: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`,
      telegram: `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(text)}`,
      email: `mailto:?subject=${encodeURIComponent(media.name)}&body=${encodeURIComponent(text + " " + shareUrl)}`,
    };
    if (platform === "copy") { navigator.clipboard.writeText(canonical); toast.success(t("detail.link_copied")); return; }
    window.open(links[platform], "_blank");
    api.post(`/media/${media.id}/view`).catch(() => {});
  };

  const submitComment = async () => {
    if (!requireAuth() || !comment.trim()) return;
    try {
      if (editing) {
        await api.put(`/comments/${editing}`, { content: comment });
        setComments((cs) => cs.map((c) => c.id === editing ? { ...c, content: comment, edited: true } : c));
        setEditing(null);
      } else {
        const { data } = await api.post(`/media/${media.id}/comments`, { content: comment, parent_id: replyTo?.id || null });
        setComments((cs) => [data, ...cs]);
        setReplyTo(null);
      }
      setComment("");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const delComment = async (id) => {
    try { await api.delete(`/comments/${id}`); setComments((cs) => cs.filter((c) => c.id !== id)); } catch (e) { toast.error(apiErr(e)); }
  };
  const reportComment = async (id) => {
    try { await api.post(`/comments/${id}/report`); toast.success(t("detail.report_success")); } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 mt-4 sm:mt-6">
      {/* Player */}
      {isTv ? (
        <VideoPlayer media={media} />
      ) : (
        <div className="relative aspect-video rounded-2xl overflow-hidden border border-border bg-gradient-to-br from-rdcgold/20 to-primary/10 flex flex-col items-center justify-center" data-testid="radio-player">
          <img src={media.banner || fallback} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
          <div className="relative flex flex-col items-center gap-4">
            <div className={`w-24 h-24 sm:w-32 sm:h-32 rounded-full border-4 border-white shadow-2xl overflow-hidden ${isThisPlaying ? "animate-[spin_8s_linear_infinite]" : ""}`}>
              <img src={media.logo || fallback} alt="" className="w-full h-full object-cover bg-white" />
            </div>
            <button
              onClick={() => playRadio(media)}
              data-testid="radio-play-pause-btn"
              className="flex items-center gap-2 rounded-full bg-rdcgold text-amber-950 px-6 py-3 font-heading font-bold shadow-lg hover:scale-105 transition-transform"
            >
              {isThisPlaying ? <><Pause size={20} fill="currentColor" /> {t("detail.listening")}</> : <><Play size={20} fill="currentColor" /> {t("detail.listen_live")}</>}
            </button>
          </div>
        </div>
      )}

      {/* Header épuré : logo + nom + catégories (sans encadré) */}
      <div className="flex items-start gap-3 mt-4">
        <img src={media.logo || fallback} alt="" className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl border border-border bg-white shrink-0" />
        <div className="min-w-0 flex-1">
          <h1 className="font-heading font-extrabold text-xl sm:text-2xl tracking-tight flex items-center gap-1.5">
            {media.name}
            {media.certified
              ? <a href={`${API}/media/${media.id}/certificate`} target="_blank" rel="noreferrer" data-testid="media-certified-badge" title="Télécharger le certificat"
                  className="inline-flex items-center gap-1 rounded-full bg-rdcgold/15 text-rdcgold px-2 py-0.5 text-[11px] font-bold shrink-0 hover:bg-rdcgold/25 transition-colors"><BadgeCheck size={14} /> {t("detail.certified")}</a>
              : media.verified && <BadgeCheck size={18} className="text-rdcblue shrink-0" />}
          </h1>
          <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
            {(media.categories || []).slice(0, 2).map((c) => (
              <span key={c} className="rounded-md bg-secondary px-2 py-0.5 text-[11px] font-medium text-secondary-foreground">{c}</span>
            ))}
            {labelFor?.(media.id) && <span data-testid="detail-top-badge" className="flex items-center gap-1 rounded-md bg-gradient-to-r from-rdcgold to-amber-500 text-amber-950 px-2 py-0.5 text-[10px] font-bold uppercase"><Trophy size={10} /> {labelFor(media.id)}</span>}
          </div>
        </div>
      </div>

      {/* Actions — simple rangée sous le lecteur */}
      <div className="flex items-center gap-2 mt-4 flex-wrap">
        <button onClick={() => act("like")} data-testid="action-like"
          className={`flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-sm font-semibold transition-colors ${media.is_liked ? "border-rdcred bg-rdcred/10 text-rdcred" : "border-border hover:border-rdcred/50"}`}>
          <Heart size={16} fill={media.is_liked ? "currentColor" : "none"} /> {(media.likes || 0).toLocaleString()}
        </button>
        <button onClick={() => act("favorite")} data-testid="action-favorite"
          className={`flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-sm font-semibold transition-colors ${media.is_favorite ? "border-rdcgold bg-rdcgold/10 text-amber-600" : "border-border hover:border-rdcgold/50"}`}>
          <Star size={16} fill={media.is_favorite ? "currentColor" : "none"} /> <span className="hidden sm:inline">{media.is_favorite ? t("detail.saved") : t("detail.favorites")}</span>
        </button>
        <a href="#comments" data-testid="action-comment"
          className="flex items-center gap-1.5 rounded-full border border-border px-3.5 py-2 text-sm font-semibold hover:border-primary/50 transition-colors">
          <MessageCircle size={16} /> {(media.comments_count || 0).toLocaleString()}
        </a>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="action-share" className="flex items-center gap-1.5 rounded-full border border-border px-3.5 py-2 text-sm font-semibold hover:border-primary/50 transition-colors">
              <Share2 size={16} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            {["whatsapp", "facebook", "x", "telegram", "email"].map((p) => (
              <DropdownMenuItem key={p} data-testid={`share-${p}`} onClick={() => share(p)} className="capitalize">{p}</DropdownMenuItem>
            ))}
            <DropdownMenuItem data-testid="share-copy" onClick={() => share("copy")}>{t("detail.copy_link")}</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <button onClick={() => setSupportOpen(true)} data-testid="support-media-btn"
          disabled={!media.certified}
          title={media.certified ? "" : "Seules les chaînes certifiées peuvent recevoir des soutiens"}
          className={`ml-auto inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${media.certified ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-secondary text-muted-foreground cursor-not-allowed opacity-60"}`}>
          <Heart size={15} fill={media.certified ? "white" : "none"} /> {t("detail.support")}
        </button>
      </div>

      {/* À propos + réseaux sociaux */}
      {(media.description || media.website || Object.values(media.socials || {}).some(Boolean)) && (
        <section className="mt-6 rounded-2xl border border-border bg-card p-5" data-testid="about-section">
          {media.description && <p className="text-sm text-muted-foreground whitespace-pre-line mb-3">{media.description}</p>}
          <div className="flex flex-wrap items-center gap-2">
            {media.website && (
              <a href={normalizeSocialHref("website", media.website)} target="_blank" rel="noreferrer" data-testid="channel-website"
                className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-sm font-medium text-muted-foreground hover:border-primary/50 hover:text-primary transition-colors">
                <Globe size={15} /> Site web
              </a>
            )}
            {Object.entries(media.socials || {}).map(([key, val]) => {
              const meta = SOCIAL_META[key];
              const href = normalizeSocialHref(key, val);
              if (!meta || !href) return null;
              const SIcon = meta.icon;
              return (
                <a key={key} href={href} target="_blank" rel="noreferrer" title={meta.label} aria-label={meta.label} data-testid={`channel-social-${key}`}
                  className={`inline-flex items-center justify-center w-9 h-9 rounded-full border border-border text-muted-foreground transition-colors hover:border-current ${meta.color}`}>
                  <SIcon size={17} />
                </a>
              );
            })}
          </div>
        </section>
      )}

      {/* Grille des émissions — À l'antenne + repliable */}
      {schedule.length > 0 && (
        <section className="mt-8" data-testid="schedule-section">
          {onAir && (
            <div className="rounded-2xl border-2 border-rdcred/40 bg-rdcred/5 p-4 mb-3 flex items-center gap-3" data-testid="on-air-now">
              <span className="live-dot flex items-center gap-1.5 rounded-full bg-rdcred px-2.5 py-1 text-[11px] font-mono font-bold uppercase text-white shrink-0"><span className="w-2 h-2 rounded-full bg-white" /> {t("detail.on_air")}</span>
              <div className="min-w-0">
                <p className="font-heading font-bold truncate">{onAir.title}</p>
                <p className="text-xs text-muted-foreground">{onAir.start_time} – {onAir.end_time}{onAir.host ? ` · ${onAir.host}` : ""}</p>
              </div>
            </div>
          )}
          <button onClick={() => setShowGrid((v) => !v)} data-testid="toggle-grid-btn"
            className="flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-semibold hover:border-primary/50 transition-colors">
            <CalendarDays size={16} className="text-primary" /> {showGrid ? t("detail.hide_grid") : t("detail.view_grid")}
            <ChevronDown size={16} className={`transition-transform ${showGrid ? "rotate-180" : ""}`} />
          </button>
          {showGrid && (
            <div className="rounded-2xl border border-border bg-card overflow-hidden mt-3" data-testid="schedule-grid">
              <div className="px-4 py-2.5 bg-secondary text-sm font-semibold flex items-center gap-2"><Clock size={15} /> {t("detail.today")} — {DAYS[now.getDay()]}</div>
              {todayGrid.length === 0 ? <p className="text-sm text-muted-foreground text-center py-6">{t("detail.no_program")}</p> :
                todayGrid.map((s) => {
                  const active = onAir && onAir.id === s.id;
                  return (
                    <div key={s.id} className={`flex items-center gap-3 px-4 py-3 border-b border-border last:border-0 ${active ? "bg-rdcred/5" : ""}`} data-testid={`schedule-row-${s.id}`}>
                      <span className="font-mono text-xs text-muted-foreground w-24 shrink-0">{s.start_time}–{s.end_time}</span>
                      <div className="min-w-0 flex-1"><p className="text-sm font-medium truncate">{s.title}</p>{s.host && <p className="text-[11px] text-muted-foreground truncate">{s.host}</p>}</div>
                      {active && <span className="text-[10px] font-bold text-rdcred uppercase shrink-0">{t("detail.ongoing")}</span>}
                      <button onClick={() => toggleReminder(s)} data-testid={`reminder-btn-${s.id}`} title={t("detail.remind")}
                        className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-colors ${reminders.has(s.id) ? "bg-primary text-white" : "hover:bg-secondary text-muted-foreground"}`}>
                        <Bell size={14} fill={reminders.has(s.id) ? "currentColor" : "none"} />
                      </button>
                    </div>
                  );
                })}
            </div>
          )}
        </section>
      )}

      {related.length > 0 && (
        <section className="mt-8" data-testid="related-section">
          <h2 className="font-heading font-bold text-lg sm:text-xl mb-4">{t("detail.discover_also")}</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
            {related.slice(0, 6).map((rm, i) => <MediaCard key={rm.id} media={rm} index={i} />)}
          </div>
        </section>
      )}

      {/* Comments */}
      <section id="comments" className="mt-8 mb-6" data-testid="comments-section">
        <h2 className="font-heading font-bold text-lg sm:text-xl mb-4 flex items-center gap-2"><MessageCircle size={20} className="text-primary" /> {t("detail.comments")} ({comments.length})</h2>

        {replyTo && (
          <div className="flex items-center justify-between rounded-lg bg-primary/10 text-primary px-3 py-2 mb-2 text-sm" data-testid="reply-banner">
            <span>{t("detail.reply_to")} <b>{replyTo.user_name}</b></span>
            <button onClick={() => setReplyTo(null)} className="text-xs hover:underline">{t("common.cancel")}</button>
          </div>
        )}
        <div className="flex gap-2 mb-6">
          <input
            data-testid="comment-input"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submitComment()}
            placeholder={user ? (replyTo ? t("detail.your_reply") : t("detail.add_comment")) : t("detail.login_to_comment")}
            className="flex-1 rounded-xl border border-border bg-card px-4 py-3 text-sm outline-none focus:border-primary"
          />
          <Button onClick={submitComment} data-testid="comment-submit-btn" className="shrink-0">
            {editing ? <Pencil size={16} /> : <Send size={16} />}
          </Button>
        </div>

        <div className="space-y-3">
          {topLevel.map((c) => (
            <div key={c.id} className="rounded-xl border border-border bg-card p-4" data-testid={`comment-${c.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="w-8 h-8 rounded-full bg-primary/15 text-primary flex items-center justify-center text-xs font-bold">{c.user_name?.[0]}</span>
                  <div>
                    <p className="text-sm font-semibold flex items-center gap-1.5">
                      {c.user_name}
                      {c.pinned && <span data-testid={`comment-pinned-${c.id}`} className="inline-flex items-center gap-0.5 rounded-full bg-rdcgold/15 text-amber-600 px-1.5 py-0.5 text-[10px] font-bold"><Pin size={10} /> {t("detail.pinned")}</span>}
                    </p>
                    <p className="text-[11px] text-muted-foreground">{new Date(c.created_at).toLocaleDateString("fr-FR")} {c.edited && `· ${t("detail.edited")}`}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {user && <button onClick={() => { setReplyTo(c); setEditing(null); setComment(""); }} data-testid={`comment-reply-${c.id}`} className="text-[11px] text-primary px-2 py-1 rounded-lg hover:bg-secondary">{t("detail.reply")}</button>}
                  {user && c.user_id === user.id && (
                    <>
                      <button onClick={() => { setEditing(c.id); setComment(c.content); setReplyTo(null); }} data-testid={`comment-edit-${c.id}`} className="w-7 h-7 rounded-lg hover:bg-secondary flex items-center justify-center text-muted-foreground"><Pencil size={13} /></button>
                      <button onClick={() => delComment(c.id)} data-testid={`comment-delete-${c.id}`} className="w-7 h-7 rounded-lg hover:bg-secondary flex items-center justify-center text-rdcred"><Trash2 size={13} /></button>
                    </>
                  )}
                  {user && c.user_id !== user.id && (
                    <button onClick={() => reportComment(c.id)} data-testid={`comment-report-${c.id}`} className="w-7 h-7 rounded-lg hover:bg-secondary flex items-center justify-center text-muted-foreground"><Flag size={13} /></button>
                  )}
                </div>
              </div>
              <p className="text-sm mt-2 leading-relaxed">{c.content}</p>

              {(repliesByParent[c.id] || []).map((r) => (
                <div key={r.id} className="mt-3 ml-6 pl-3 border-l-2 border-border" data-testid={`comment-${r.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${r.is_manager_reply ? "bg-rdcblue/15 text-rdcblue" : "bg-rdcgold/15 text-rdcgold"}`}>{r.user_name?.[0]}</span>
                      <div><p className="text-xs font-semibold flex items-center gap-1">{r.user_name}{r.is_manager_reply && <BadgeCheck size={12} className="text-rdcblue" />}</p><p className="text-[10px] text-muted-foreground">{new Date(r.created_at).toLocaleDateString("fr-FR")}{r.is_manager_reply ? ` · ${t("detail.official_reply")}` : ""}</p></div>
                    </div>
                    {user && (r.user_id === user.id
                      ? <button onClick={() => delComment(r.id)} data-testid={`comment-delete-${r.id}`} className="w-6 h-6 rounded-lg hover:bg-secondary flex items-center justify-center text-rdcred"><Trash2 size={12} /></button>
                      : <button onClick={() => reportComment(r.id)} data-testid={`comment-report-${r.id}`} className="w-6 h-6 rounded-lg hover:bg-secondary flex items-center justify-center text-muted-foreground"><Flag size={12} /></button>)}
                  </div>
                  <p className="text-xs mt-1 leading-relaxed">{r.content}</p>
                </div>
              ))}
            </div>
          ))}
          {comments.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">{t("detail.be_first")}</p>}
        </div>
      </section>

      <SupportModal open={supportOpen} onOpenChange={setSupportOpen} media={media} />
    </div>
  );
}
